import { action, computed, reaction, makeObservable } from 'mobx';
import {contentLookupService, UserStatus, userStatusService} from '../../domain';
import {routerStore} from '../../lib/components';
import {Disposers} from '../../lib/helpers';
import {TreeService} from './menu/models/tree-service';
import {SidebarHelper} from './sidebar-helper';

export class SidebarStore {
  @computed
  get userStatus(): UserStatus {
    return userStatusService.status;
  }

  @computed
  get showContentManagementMenu(): boolean {
    return userStatusService.status.roles.ContentManager || userStatusService.status.roles.Teacher;
  }

  @computed
  get showAdministratorMenu(): boolean {
    return userStatusService.status.roles.Administrator;
  }

  @computed
  get showTeacherMenu(): boolean {
    return userStatusService.status.roles.Teacher;
  }

  @computed
  get showStudentMenu(): boolean {
    return userStatusService.status.roles.Student;
  }

  systemSettingsMenu: TreeService;
  userManagementMenu: TreeService;
  classroomManagementMenu: TreeService;
  contentManagementMenu: TreeService;
  studentMenu: TreeService;
  teacherMenu: TreeService;

  private readonly disposers = new Disposers();

  constructor() {
    makeObservable(this);
    this.systemSettingsMenu = new TreeService();
    this.systemSettingsMenu.setItems(SidebarHelper.systemSettingsItems);
    this.userManagementMenu = new TreeService();
    this.userManagementMenu.setItems(SidebarHelper.userManagementItems);
    this.classroomManagementMenu = new TreeService();
    this.classroomManagementMenu.setItems(SidebarHelper.classroomManagementItems);
    this.contentManagementMenu = new TreeService();
    this.studentMenu = new TreeService();
    this.studentMenu.setItems(SidebarHelper.studentItems);
    this.teacherMenu = new TreeService();
    this.teacherMenu.setItems(SidebarHelper.teacherItems);
    this.setup();
  }

  @action
  loadLookup = () => {
    contentLookupService.load();
  };

  private setup() {
    this.disposers.add(reaction(
      () => this.userStatus,
      this.onUserStatusChange,
      {fireImmediately: true}
    ));
    this.disposers.add(reaction(
      () => contentLookupService.state.state,
      this.onContentLookupStateChange,
      {fireImmediately: true}
    ));
    this.disposers.add(reaction(
      () => routerStore.path,
      this.onRouteChange,
      {fireImmediately: true}
    ));
  }

  @action
  private onUserStatusChange = () => {
    if (this.showContentManagementMenu || this.showTeacherMenu)
      this.loadLookup();
  };

  @action
  private onContentLookupStateChange = () => {
    const state = contentLookupService.state;
    if (state.isResolved) {
      const lookup = state.value.lookup;
      const items = SidebarHelper.prepareContentManagementItems(lookup);
      this.contentManagementMenu.setItems(items);
    }
    else {
      this.contentManagementMenu.setItems([]);
    }
  };

  @action
  private onRouteChange = () => {
    const route = routerStore.path;
    this.contentManagementMenu.setCurrentRoute(route);
    this.userManagementMenu.setCurrentRoute(route);
    this.teacherMenu.setCurrentRoute(route);
    this.studentMenu.setCurrentRoute(route);
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
